/**
 * Glowy Studio — "Design it yourself" çerçeve editörü.
 *
 * Kullanıcı bir tuval üstünde çerçevesini KENDİ kurar: ürün alanını yerleştirip
 * boyutlandırır, logosunu koyar, yazı yazar, kutu/daire ekler. Çıkan şey bir PNG
 * değil, bir TASARIM BELGESİdir; motor onu her ürün için yeniden çizer. Bu
 * yüzden yazılar ürünün kendi verisini taşıyabilir ({{title}}, {{price}},
 * {{discount}}) ve kullanıcı tasarımı sonradan geri açıp düzenleyebilir.
 *
 * 🔑🔑 Buradaki çizim ile motorun çizimi AYNI Lato dosyalarından besleniyor
 * (`/pixel/fonts/lato` PIL'in okuduğu dizini aynen sunuyor) ve satır kutusu
 * CSS'in half-leading kuralıyla hizalandı. Yine de satır SARMASI iki ayrı
 * ölçüm motorundan geçiyor; bu yüzden "Real preview" düğmesi var — kullanıcı
 * kaydetmeden önce gerçek kareyi görebilmeli.
 *
 * 🔑 Belgeyi DOĞRULAYAN taraf motor: kaydetme 422 dönerse hangi kuralın
 * düştüğü aynen gösterilir, burada ikinci bir kural seti yazılmaz.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  X, Type, Square, Circle, Image as ImageIcon, Package, Trash2, Copy,
  ChevronUp, ChevronDown, Loader2, Save, Eye, Shuffle, Upload, Undo2,
  AlertTriangle, Layers, Sparkles, Tag, Percent, BadgeCheck,
} from 'lucide-react';
import {
  CANVAS_PRESETS, FONT_FAMILY, LAYER_LABEL, WEIGHT_LABEL, clamp, fillFields,
  fontFaceCss, makeLayer, newId, productFields, starterDesigns,
} from './designDoc';

const API = 'https://api.glowytics.com';
const BASE = `${API}/api/feed-builder`;

const HEX = /^#[0-9a-fA-F]{6}$/;
const SNAP = 8;            // tuval birimi cinsinden yapışma payı
const MAX_HISTORY = 40;

/* Lato'yu belgeye BİR KEZ ekle. Editör kapanınca da kalsın — tekrar açılışta
   yazı tipi yeniden indirilmesin, ilk kare "yanlış fontla" çizilmesin. */
let fontsInjected = false;
function useLatoFonts() {
  useEffect(() => {
    if (fontsInjected) return;
    const el = document.createElement('style');
    el.setAttribute('data-glowy-lato', '1');
    el.textContent = fontFaceCss();
    document.head.appendChild(el);
    fontsInjected = true;
  }, []);
}

// =========================================================== küçük kontroller
const Row = ({ label, children, hint }) => (
  <div className="mb-2.5">
    <div className="flex items-center justify-between mb-1">
      <span className="text-[11px] font-medium text-gray-600">{label}</span>
      {hint && <span className="text-[10px] text-gray-400">{hint}</span>}
    </div>
    {children}
  </div>
);

const NumBox = ({ value, onChange, min = -9999, max = 9999, step = 1, suffix }) => (
  <div className="relative">
    <input
      type="number" value={value} min={min} max={max} step={step}
      onChange={(e) => {
        const n = parseFloat(e.target.value);
        onChange(Number.isFinite(n) ? clamp(n, min, max) : value);
      }}
      className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 pr-6
                 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
    {suffix && <span className="absolute right-2 top-1.5 text-[10px] text-gray-400">{suffix}</span>}
  </div>
);

const ColorBox = ({ value, onChange, allowEmpty }) => (
  <div className="flex items-center gap-1.5">
    <input
      type="color" value={HEX.test(value) ? value : '#000000'}
      onChange={(e) => onChange(e.target.value)}
      className="w-8 h-8 rounded border border-gray-300 cursor-pointer bg-white p-0.5 shrink-0" />
    <input
      type="text" value={value || ''} placeholder={allowEmpty ? 'none' : '#000000'}
      onChange={(e) => {
        const v = e.target.value.trim();
        if (!v && allowEmpty) return onChange('');
        onChange(v.startsWith('#') ? v : `#${v}`);
      }}
      className="w-full text-xs font-mono border border-gray-300 rounded px-2 py-1.5
                 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
    {allowEmpty && value && (
      <button onClick={() => onChange('')} title="Remove"
        className="text-gray-400 hover:text-red-600 shrink-0"><X className="w-3.5 h-3.5" /></button>
    )}
  </div>
);

const Seg = ({ options, value, onChange }) => (
  <div className="flex rounded-lg border border-gray-300 overflow-hidden">
    {options.map((o) => (
      <button key={o.value} onClick={() => onChange(o.value)} title={o.title}
        className={`flex-1 text-[11px] py-1.5 transition ${
          value === o.value ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
        {o.label}
      </button>
    ))}
  </div>
);

// ================================================================ katman çizimi
/**
 * Bir katmanı tuvale çizer. DOM kuralları motorun PIL çizimiyle eşleşecek
 * şekilde seçildi — kutu modeli `border-box` (PIL de çerçeveyi kutunun İÇİNE
 * çiziyor), satır kutusu `line-height` (PIL half-leading ile hizalandı).
 */
function LayerView({ layer: l, fields, productImg, assets, selected, onMeasure }) {
  const ref = useRef(null);

  // Yazının yüksekliği belgede yok, DOM'da ölçülür: seçim çerçevesi ve
  // katman listesi bunu bilmeli.
  useEffect(() => {
    if (l.type !== 'text' || !ref.current || !onMeasure) return undefined;
    const el = ref.current;
    const ro = new ResizeObserver(() => onMeasure(l.id, el.offsetHeight));
    ro.observe(el);
    onMeasure(l.id, el.offsetHeight);
    return () => ro.disconnect();
  }, [l.id, l.type, onMeasure]);

  // Koşullu katman: alan o üründe boşsa motor katmanı hiç çizmiyor. Editörde
  // silmek yerine SOLUK gösteriyoruz — kullanıcı katmanın orada olduğunu
  // görmeli, yoksa "kayboldu" der.
  const hiddenByField = l.showIf && !String(fields[l.showIf] ?? '').trim();

  const base = {
    position: 'absolute',
    left: l.x, top: l.y,
    opacity: (l.opacity ?? 1) * (hiddenByField ? 0.22 : 1),
    transform: l.rotation ? `rotate(${l.rotation}deg)` : undefined,
    pointerEvents: 'none',
  };

  if (l.type === 'text') {
    const { text, hasField, allEmpty } = fillFields(l.text, fields);
    const track = (l.tracking || 0) * l.size;
    // CSS harf aralığını SON harften sonra da ekler, PIL eklemez. Ortalı ve
    // sağa dayalı yazı bu yüzden yarım/tam aralık kadar kayıyor — geri it.
    const nudge = l.align === 'center' ? track / 2 : (l.align === 'right' ? track : 0);
    const clampStyle = l.maxLines
      ? { display: '-webkit-box', WebkitLineClamp: l.maxLines, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
      : {};
    return (
      <div ref={ref} style={{
        ...base,
        width: l.w,
        opacity: base.opacity * (hasField && allEmpty ? 0.25 : 1),
        fontFamily: `'${FONT_FAMILY}', system-ui, sans-serif`,
        fontWeight: l.weight,
        fontStyle: l.italic ? 'italic' : 'normal',
        fontSize: l.size,
        lineHeight: `${l.size * l.lineHeight}px`,
        letterSpacing: track ? `${track}px` : undefined,
        marginLeft: nudge || undefined,
        color: l.color,
        textAlign: l.align,
        textTransform: l.uppercase ? 'uppercase' : undefined,
        textDecoration: l.strike ? 'line-through' : undefined,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'break-word',
        outline: selected ? '2px solid #4f46e5' : undefined,
        outlineOffset: 2,
        ...clampStyle,
      }}>
        {text || ' '}
      </div>
    );
  }

  const boxed = {
    ...base,
    width: l.w, height: l.h, boxSizing: 'border-box',
    outline: selected ? '2px solid #4f46e5' : undefined,
    outlineOffset: 2,
  };

  if (l.type === 'rect' || l.type === 'ellipse') {
    return <div style={{
      ...boxed,
      background: l.fill,
      borderRadius: l.type === 'ellipse' ? '50%' : (l.radius || 0),
      border: l.stroke && l.strokeWidth ? `${l.strokeWidth}px solid ${l.stroke}` : undefined,
    }} />;
  }

  if (l.type === 'image') {
    const url = assets[l.src];
    if (!url) {
      return (
        <div style={{ ...boxed, border: '2px dashed #cbd5e1', display: 'grid', placeItems: 'center' }}>
          <span style={{ fontSize: Math.min(28, l.h / 4), color: '#94a3b8' }}>image</span>
        </div>
      );
    }
    return <img src={url} alt="" draggable={false} style={{
      ...boxed, objectFit: l.fit === 'stretch' ? 'fill' : l.fit,
    }} />;
  }

  // ürün alanı
  return (
    <div style={{
      ...boxed,
      background: l.bg || 'transparent',
      borderRadius: l.radius || 0,
      overflow: 'hidden',
      display: 'grid', placeItems: 'center',
      border: productImg ? undefined : '3px dashed #94a3b8',
    }}>
      {productImg
        ? <img src={productImg} alt="" draggable={false} style={{
          width: '100%', height: '100%', objectFit: l.fit === 'stretch' ? 'fill' : l.fit,
        }} />
        : (
          <div style={{ textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: Math.min(40, l.w / 8), fontWeight: 700 }}>PRODUCT</div>
            <div style={{ fontSize: Math.min(24, l.w / 14) }}>drops in here</div>
          </div>
        )}
    </div>
  );
}

// ==================================================================== editör
export default function FrameEditor({
  open, onClose, brand, brandHeader, products = [], onSaved, editFrame,
}) {
  useLatoFonts();

  const [doc, setDoc] = useState(null);
  const [name, setName] = useState('My frame');
  const [selId, setSelId] = useState(null);
  const [measured, setMeasured] = useState({});
  const [assets, setAssets] = useState({});        // "asset:<id>" → url
  const [brandLogoAsset, setBrandLogoAsset] = useState(null);  // marka logosu bir kez indirilir
  // Logo adresi engelliyse (403) tek yol dosyadan yüklemek — düğme ona döner.
  const [logoNeedsUpload, setLogoNeedsUpload] = useState(false);
  const logoPlaced = useRef(false);   // yeni tasarıma logo BİR KEZ kendiliğinden konur
  const [product, setProduct] = useState(null);
  const [errors, setErrors] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [realUrl, setRealUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [guides, setGuides] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [editingText, setEditingText] = useState(false);

  const history = useRef([]);
  const stageRef = useRef(null);
  const wrapRef = useRef(null);
  const fileRef = useRef(null);
  const logoFileRef = useRef(null);
  const [area, setArea] = useState({ w: 800, h: 800 });

  const fields = useMemo(() => productFields(product, brand?.name), [product, brand]);
  const sel = doc?.layers.find((l) => l.id === selId) || null;
  const productImg = product?.thumb || null;

  // ------------------------------------------------------------- açılış
  useEffect(() => {
    if (!open) return;
    setErrors(null);
    setRealUrl(null);
    history.current = [];
    if (editFrame?.id) {
      setLoadingDoc(true);
      setName(editFrame.name || 'My frame');
      fetch(`${BASE}/design/${editFrame.id}`, { credentials: 'include', headers: brandHeader })
        .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
        .then(({ ok, j }) => {
          if (!ok) throw new Error(j.error || 'Could not open that design');
          setDoc(j.design);
          setSelId(j.design.layers.find((l) => l.type === 'product')?.id || null);
        })
        .catch((e) => { toast.error(e.message); onClose(); })
        .finally(() => setLoadingDoc(false));
    } else {
      setDoc(null);                 // başlangıç tasarımı seçtirilecek
      setName('My frame');
      setSelId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editFrame?.id]);

  // ilk ürünü canlı örnek olarak al
  useEffect(() => {
    if (!open || product || !products.length) return;
    const withImg = products.filter((p) => p.thumb);
    if (withImg.length) setProduct(withImg[Math.floor(Math.random() * withImg.length)]);
  }, [open, products, product]);

  // sayfayı kilitle
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // tuval alanını ölç
  useEffect(() => {
    if (!open || !wrapRef.current) return undefined;
    const el = wrapRef.current;
    const ro = new ResizeObserver(() => setArea({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setArea({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, [open, doc]);

  const scale = doc
    ? Math.min((area.w - 64) / doc.canvas.w, (area.h - 64) / doc.canvas.h, 1)
    : 1;

  // ---------------------------------------------------------- belge işlemleri
  const pushHistory = useCallback(() => {
    if (!doc) return;
    history.current.push(JSON.stringify(doc));
    if (history.current.length > MAX_HISTORY) history.current.shift();
  }, [doc]);

  const undo = useCallback(() => {
    const prev = history.current.pop();
    if (!prev) return;
    setDoc(JSON.parse(prev));
    setRealUrl(null);
  }, []);

  /** Katmanı günceller. `commit` false ise geçmişe yazmaz (sürükleme sırasında
   *  her piksel için geri alma noktası açmak geçmişi anlamsızlaştırıyor). */
  const patch = useCallback((id, p, commit = true) => {
    if (commit) pushHistory();
    setDoc((d) => (d ? {
      ...d, layers: d.layers.map((l) => (l.id === id ? { ...l, ...p } : l)),
    } : d));
    setRealUrl(null);
  }, [pushHistory]);

  const patchCanvas = useCallback((p) => {
    pushHistory();
    setDoc((d) => (d ? { ...d, canvas: { ...d.canvas, ...p } } : d));
    setRealUrl(null);
  }, [pushHistory]);

  const addLayer = useCallback((type, extra = {}) => {
    if (!doc) return;
    pushHistory();
    const { w, h } = doc.canvas;
    const defaults = {
      text: { x: Math.round(w * 0.1), y: Math.round(h * 0.1), w: Math.round(w * 0.8), size: Math.round(w / 22) },
      rect: { x: Math.round(w * 0.1), y: Math.round(h * 0.1), w: Math.round(w * 0.35), h: Math.round(w * 0.18) },
      ellipse: { x: Math.round(w * 0.1), y: Math.round(h * 0.1), w: Math.round(w * 0.2), h: Math.round(w * 0.2) },
      image: { x: Math.round(w * 0.1), y: Math.round(h * 0.06), w: Math.round(w * 0.3), h: Math.round(w * 0.12) },
    }[type] || {};
    const layer = makeLayer(type, { ...defaults, ...extra });
    setDoc((d) => ({ ...d, layers: [...d.layers, layer] }));
    setSelId(layer.id);
    setRealUrl(null);
  }, [doc, pushHistory]);

  // 🔑 16 Eyl 2026 (Efe): ürün verisine bağlı alanlar tek tıkla eklenebilsin —
  // eskiden "Text" ekleyip içine alan çipi tıklamak gerekiyordu, bulunmuyordu.
  // Eklenen katman sıradan bir metin katmanı: {{alan}} motorda her üründe dolar.
  const addField = useCallback((key) => {
    if (!doc) return;
    const { w, h } = doc.canvas;
    const u = w / 1080;
    const r = (v) => Math.round(v * u);
    const preset = {
      title: { x: r(80), y: h - r(260), w: w - r(160), text: '{{title}}', size: r(44),
               weight: 700, color: '#111111', maxLines: 2 },
      price: { x: w - r(480), y: h - r(170), w: r(400), text: '{{price}}', size: r(64),
               weight: 800, color: '#e11d48', align: 'right' },
      list_price: { x: w - r(480), y: h - r(230), w: r(400), text: '{{list_price}}', size: r(38),
                    weight: 500, color: '#9ca3af', align: 'right', strike: true, showIf: 'list_price' },
      discount: { x: w - r(300), y: r(80), w: r(220), text: '%{{discount}}', size: r(64),
                  weight: 800, color: '#e11d48', align: 'center', showIf: 'discount' },
      brand: { x: r(80), y: r(80), w: w - r(160), text: '{{brand}}', size: r(36),
               weight: 800, color: '#111111', uppercase: true },
    }[key];
    if (preset) addLayer('text', preset);
  }, [doc, addLayer]);

  const removeLayer = useCallback((id) => {
    const l = doc?.layers.find((x) => x.id === id);
    if (!l) return;
    if (l.type === 'product') {
      toast.error('The product area cannot be removed — it is where the product lands.');
      return;
    }
    pushHistory();
    setDoc((d) => ({ ...d, layers: d.layers.filter((x) => x.id !== id) }));
    setSelId(null);
    setRealUrl(null);
  }, [doc, pushHistory]);

  const duplicateLayer = useCallback((id) => {
    const l = doc?.layers.find((x) => x.id === id);
    if (!l || l.type === 'product') return;
    pushHistory();
    const copy = { ...l, id: newId(), x: l.x + 24, y: l.y + 24 };
    setDoc((d) => ({ ...d, layers: [...d.layers, copy] }));
    setSelId(copy.id);
    setRealUrl(null);
  }, [doc, pushHistory]);

  const moveZ = useCallback((id, dir) => {
    pushHistory();
    setDoc((d) => {
      const i = d.layers.findIndex((l) => l.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= d.layers.length) return d;
      const layers = [...d.layers];
      [layers[i], layers[j]] = [layers[j], layers[i]];
      return { ...d, layers };
    });
    setRealUrl(null);
  }, [pushHistory]);

  const onMeasure = useCallback((id, h) => {
    setMeasured((m) => (m[id] === h ? m : { ...m, [id]: h }));
  }, []);

  const boxOf = useCallback((l) => ({
    x: l.x, y: l.y, w: l.w,
    h: l.type === 'text' ? (measured[l.id] || Math.round(l.size * l.lineHeight)) : l.h,
  }), [measured]);

  // ------------------------------------------------------ sürükle / boyutlandır
  const drag = useRef(null);

  const onPointerDown = (e, layer, handle) => {
    if (editingText) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setSelId(layer.id);
    pushHistory();
    drag.current = {
      id: layer.id, handle,
      sx: e.clientX, sy: e.clientY,
      start: { ...boxOf(layer) },
      type: layer.type,
    };
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d || !doc) return;
    const dx = (e.clientX - d.sx) / scale;
    const dy = (e.clientY - d.sy) / scale;
    const { w: cw, h: ch } = doc.canvas;
    const min = 24;

    if (!d.handle) {
      let x = Math.round(d.start.x + dx);
      let y = Math.round(d.start.y + dy);
      const g = [];
      // Tuvalin kenarlarına ve ORTASINA yapış — göz kararı ortalamak yerine
      // gerçekten ortalansın.
      const xs = [[0, 0], [Math.round((cw - d.start.w) / 2), cw / 2], [cw - d.start.w, cw]];
      const ys = [[0, 0], [Math.round((ch - d.start.h) / 2), ch / 2], [ch - d.start.h, ch]];
      for (const [tx, line] of xs) if (Math.abs(x - tx) < SNAP / scale) { x = tx; g.push({ v: line }); }
      for (const [ty, line] of ys) if (Math.abs(y - ty) < SNAP / scale) { y = ty; g.push({ h: line }); }
      setGuides(g.length ? g : null);
      patch(d.id, { x, y }, false);
      return;
    }

    const H = d.handle;
    let { x, y, w, h } = d.start;
    if (H.includes('e')) w = Math.max(min, Math.round(d.start.w + dx));
    if (H.includes('s')) h = Math.max(min, Math.round(d.start.h + dy));
    if (H.includes('w')) {
      w = Math.max(min, Math.round(d.start.w - dx));
      x = d.start.x + d.start.w - w;
    }
    if (H.includes('n')) {
      h = Math.max(min, Math.round(d.start.h - dy));
      y = d.start.y + d.start.h - h;
    }
    // Shift → oranı koru (köşelerden)
    if (e.shiftKey && H.length === 2 && d.type !== 'text') {
      const r = d.start.w / d.start.h;
      h = Math.round(w / r);
      if (H.includes('n')) y = d.start.y + d.start.h - h;
    }
    patch(d.id, d.type === 'text' ? { x, w } : { x, y, w, h }, false);
  };

  const onPointerUp = () => { drag.current = null; setGuides(null); };

  // klavye
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (editingText) { if (e.key === 'Escape') setEditingText(false); return; }
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); return; }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'd' && selId) {
        e.preventDefault(); duplicateLayer(selId); return;
      }
      if (e.key === 'Escape') { if (selId) setSelId(null); else onClose(); return; }
      if (!selId || !sel) return;
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeLayer(selId); return; }
      const step = e.shiftKey ? 10 : 1;
      const nudge = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (nudge) { e.preventDefault(); patch(selId, { x: sel.x + nudge[0], y: sel.y + nudge[1] }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, selId, sel, editingText, undo, duplicateLayer, removeLayer, patch, onClose]);

  // ------------------------------------------------------------- görsel yükle
  const uploadAsset = useCallback(async (file) => {
    if (!file || !brandHeader) return null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${BASE}/design/assets`, {
        method: 'POST', credentials: 'include', headers: brandHeader, body: fd,
      });
      const j = await r.json();
      if (!r.ok) { toast.error((j.errors || [])[0] || j.error || 'Upload failed'); return null; }
      setAssets((a) => ({ ...a, [j.asset.src]: j.asset.url }));
      return j.asset;
    } catch (e) {
      toast.error(e.message); return null;
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [brandHeader]);

  const addImageFromFile = useCallback(async (file) => {
    const a = await uploadAsset(file);
    if (!a || !doc) return;
    // Yüklenen görselin ORANINI koru: kullanıcı logosunu koyup elle
    // düzeltmek zorunda kalmasın.
    const w = Math.round(doc.canvas.w * 0.3);
    addLayer('image', { src: a.src, w, h: Math.max(1, Math.round(w * (a.h / a.w))) });
  }, [uploadAsset, addLayer, doc]);

  // Markanın logosu. 🔑🔑 Artık markaya ait YEREL kopyadan gelir
  // (`/brand-logo`): bir kez indirildi mi (ya da dosyadan yüklendi mi) her
  // açılışta kendiliğinden hazır olur. 🔴 Bazı markaların logo adresi bot
  // koruması arkasında ve sunucumuzun IP'si engelli (403) — o markalarda düğme
  // "Upload logo"ya döner, yüklenen dosya markanın logosu olarak SAKLANIR.
  const placeLogo = useCallback((a, opts = {}) => {
    if (!a?.w || !a?.h || !doc) return;
    const w = Math.round(doc.canvas.w * (opts.w || 0.28));
    addLayer('image', {
      src: a.src, w, h: Math.max(1, Math.round(w * (a.h / a.w))),
      ...(opts.x != null ? { x: opts.x } : {}), ...(opts.y != null ? { y: opts.y } : {}),
    });
  }, [addLayer, doc]);

  const uploadBrandLogo = useCallback(async (file) => {
    if (!file || !brandHeader) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${BASE}/brand-logo`, {
        method: 'POST', credentials: 'include', headers: brandHeader, body: fd,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error((j.errors || [])[0] || j.error || 'Could not save the logo'); return; }
      setAssets((a) => ({ ...a, [j.asset.src]: j.asset.url }));
      setBrandLogoAsset(j.asset);
      setLogoNeedsUpload(false);
      placeLogo(j.asset);
      toast.success('Logo saved for this brand');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
      if (logoFileRef.current) logoFileRef.current.value = '';
    }
  }, [brandHeader, placeLogo]);

  const addBrandLogo = useCallback(() => {
    if (brandLogoAsset) { placeLogo(brandLogoAsset); return; }
    logoFileRef.current?.click();
  }, [brandLogoAsset, placeLogo]);

  // Editör açılırken logo KENDİLİĞİNDEN çözülür — kullanıcı aramasın.
  useEffect(() => {
    if (!open || !brandHeader || brandLogoAsset) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/brand-logo`, { credentials: 'include', headers: brandHeader });
        const j = await r.json().catch(() => ({}));
        if (!alive) return;
        if (r.ok && j.asset) {
          setAssets((a) => ({ ...a, [j.asset.src]: j.asset.url }));
          setBrandLogoAsset(j.asset);
          setLogoNeedsUpload(false);
        } else {
          setLogoNeedsUpload(!!j.canUpload);
        }
      } catch { if (alive) setLogoNeedsUpload(true); }
    })();
    return () => { alive = false; };
  }, [open, brandHeader, brandLogoAsset]);

  // YENİ tasarımda logo kendiliğinden sol üste konur (düzenlenen tasarıma
  // dokunulmaz — kullanıcı oradan silmişse geri gelmemeli).
  useEffect(() => {
    if (!open || editFrame || !doc || !brandLogoAsset || logoPlaced.current) return;
    if (doc.layers.some((l) => l.type === 'image' && l.src === brandLogoAsset.src)) return;
    logoPlaced.current = true;
    const w = Math.round(doc.canvas.w * 0.22);
    placeLogo(brandLogoAsset, {
      w: 0.22,
      x: Math.round(doc.canvas.w * 0.06),
      y: Math.round(doc.canvas.w * 0.06),
    });
    void w;
  }, [open, editFrame, doc, brandLogoAsset, placeLogo]);

  useEffect(() => { if (!open) logoPlaced.current = false; }, [open]);

  // düzenlemeye açılan tasarımın varlıkları — url'leri kimlikten türet
  useEffect(() => {
    if (!doc || !brand?.id) return;
    const missing = doc.layers
      .filter((l) => l.type === 'image' && l.src.startsWith('asset:') && !assets[l.src]);
    if (!missing.length) return;
    setAssets((a) => {
      const next = { ...a };
      for (const l of missing) {
        next[l.src] = `${API}/pixel/feed-builder/assets/${brand.id}/${l.src.slice(6)}.png`;
      }
      return next;
    });
  }, [doc, brand, assets]);

  // --------------------------------------------------------- gerçek önizleme
  const realPreview = useCallback(async () => {
    if (!doc) return;
    setPreviewing(true);
    setErrors(null);
    try {
      const r = await fetch(`${BASE}/design/preview`, {
        method: 'POST', credentials: 'include',
        headers: { ...brandHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ design: doc, groupId: product?.groupId || undefined }),
      });
      const j = await r.json();
      if (!r.ok) {
        if (j.errors?.length) setErrors(j.errors);
        toast.error(j.error || 'Could not render');
        return;
      }
      setRealUrl(j.url);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPreviewing(false);
    }
  }, [doc, brandHeader, product]);

  // ----------------------------------------------------------------- kaydet
  const save = useCallback(async () => {
    if (!doc) return;
    setSaving(true);
    setErrors(null);
    try {
      const r = await fetch(`${BASE}/design`, {
        method: 'POST', credentials: 'include',
        headers: { ...brandHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ design: doc, name, id: editFrame?.id || undefined }),
      });
      const j = await r.json();
      if (!r.ok) {
        if (j.errors?.length) setErrors(j.errors);
        toast.error(j.error || 'Could not save');
        return;
      }
      toast.success(j.updated ? 'Design updated' : 'Frame added');
      onSaved?.(j.frame);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }, [doc, name, brandHeader, editFrame, onSaved, onClose]);

  if (!open) return null;

  // ------------------------------------------------- başlangıç tasarımı seçimi
  if (!doc) {
    return (
      <div className="fixed inset-0 z-[70] bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6">
          {loadingDoc ? (
            <div className="py-20 text-center text-gray-500 flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Opening your design…
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-1">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Design your own frame</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Pick a shape and a starting point — every part of it is yours to move.
                  </p>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <StarterPicker onPick={(d, n) => { setDoc(d); setName(n); setSelId(null); }} />
            </>
          )}
        </div>
      </div>
    );
  }

  const { w: cw, h: ch } = doc.canvas;

  return (
    <div className="fixed inset-0 z-[70] bg-gray-900/70 backdrop-blur-sm flex flex-col">
      {/* ------------------------------------------------------------ üst bar */}
      <div className="h-14 bg-white border-b border-gray-200 flex items-center gap-3 px-4 shrink-0">
        <Sparkles className="w-4 h-4 text-indigo-600 shrink-0" />
        <input
          value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
          className="text-sm font-semibold text-gray-900 border border-transparent hover:border-gray-300
                     focus:border-indigo-400 rounded px-2 py-1 w-56 focus:outline-none" />
        <span className="text-xs text-gray-400">{cw}×{ch}</span>

        <div className="flex-1" />

        <button onClick={undo} disabled={!history.current.length} title="Undo (⌘Z)"
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30">
          <Undo2 className="w-4 h-4" />
        </button>
        <button onClick={() => { const w = products.filter((p) => p.thumb); if (w.length) { setProduct(w[Math.floor(Math.random() * w.length)]); setRealUrl(null); } }}
          disabled={!products.length}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg
                     hover:bg-gray-50 disabled:opacity-40">
          <Shuffle className="w-3.5 h-3.5" /> Another product
        </button>
        <button onClick={realPreview} disabled={previewing}
          title="Draw it on the server, exactly as the feed will"
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-indigo-300
                     text-indigo-700 rounded-lg hover:bg-indigo-50 disabled:opacity-40">
          {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
          Real preview
        </button>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                     bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {editFrame?.id ? 'Save changes' : 'Add this frame'}
        </button>
        <button onClick={onClose} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* ------------------------------------------------------- sol: ekle + katmanlar */}
        <div className="w-56 bg-white border-r border-gray-200 flex flex-col shrink-0">
          <div className="p-3 border-b border-gray-100">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Add</div>
            <div className="grid grid-cols-2 gap-1.5">
              <AddBtn icon={Type} label="Text" onClick={() => addLayer('text')} />
              <AddBtn icon={Square} label="Box" onClick={() => addLayer('rect')} />
              <AddBtn icon={Circle} label="Circle" onClick={() => addLayer('ellipse')} />
              <AddBtn icon={ImageIcon} label="Image" onClick={() => fileRef.current?.click()}
                busy={uploading} />
            </div>
            <input ref={fileRef} type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
              onChange={(e) => addImageFromFile(e.target.files?.[0])} />
            <input ref={logoFileRef} type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
              onChange={(e) => uploadBrandLogo(e.target.files?.[0])} />
            <button onClick={addBrandLogo} disabled={uploading}
              title={brandLogoAsset
                ? 'Place your brand logo'
                : 'Upload your logo once — it is kept for this brand'}
              className="mt-1.5 w-full flex items-center justify-center gap-2 px-2 py-1.5 text-[11px]
                         border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50
                         disabled:opacity-40">
              {uploading
                ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                : brandLogoAsset
                  ? <img src={brandLogoAsset.url} alt="" className="h-4 max-w-[52px] object-contain shrink-0" />
                  : <Upload className="w-3.5 h-3.5 shrink-0" />}
              {brandLogoAsset ? 'Brand logo' : 'Upload logo'}
            </button>
            {logoNeedsUpload && !brandLogoAsset && (
              <p className="mt-1 text-[10px] text-amber-700 leading-snug">
                Your logo address is behind bot protection, so we cannot fetch it. Upload the file
                once and it stays saved for this brand.
              </p>
            )}
          </div>

          <div className="p-3 border-b border-gray-100">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Canvas</div>
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              {CANVAS_PRESETS.map((p) => (
                <button key={p.key} title={p.hint}
                  onClick={() => patchCanvas({ w: p.w, h: p.h })}
                  className={`text-[11px] py-1.5 rounded-lg border transition ${
                    cw === p.w && ch === p.h
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700 font-medium'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                  {p.key}
                </button>
              ))}
            </div>
            <Row label="Background">
              <ColorBox value={doc.canvas.bg} onChange={(v) => HEX.test(v) && patchCanvas({ bg: v })} />
            </Row>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Product data
            </div>
            <div className="grid grid-cols-2 gap-1.5 mb-4">
              {PRODUCT_FIELDS.map((f) => (
                <button key={f.key} onClick={() => addField(f.key)}
                  title={fields[f.key] ? `Right now: ${f.key === 'discount' ? '%' : ''}${fields[f.key]}` : 'Empty for this product'}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-indigo-200
                             text-[11px] text-indigo-700 hover:bg-indigo-50 text-left">
                  <f.icon className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{f.label}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500
                            uppercase tracking-wide mb-2">
              <Layers className="w-3 h-3" /> Layers
            </div>
            {/* Üstteki katman ÜSTTE çizilir — liste ters sırada, yığının
                gözle göründüğü sıra bu. */}
            {[...doc.layers].reverse().map((l) => (
              <button key={l.id} onClick={() => setSelId(l.id)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs mb-0.5
                            transition ${l.id === selId
                  ? 'bg-indigo-50 text-indigo-800 ring-1 ring-indigo-300'
                  : 'text-gray-600 hover:bg-gray-50'}`}>
                <LayerIcon type={l.type} />
                <span className="truncate flex-1">{LAYER_LABEL(l)}</span>
                {l.showIf && <span className="text-[9px] text-amber-600 shrink-0">if {l.showIf}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* ------------------------------------------------------------- tuval */}
        <div ref={wrapRef} className="flex-1 min-w-0 overflow-hidden relative bg-gray-800/40
                                      flex items-center justify-center"
          onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
          onPointerDown={() => { if (!editingText) setSelId(null); }}>
          {realUrl ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 z-10 bg-gray-900/80">
              <img src={realUrl} alt="Real render"
                style={{ maxHeight: '80%', maxWidth: '90%' }}
                className="rounded-lg shadow-2xl" />
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-300">
                  This is the real output — drawn by the same engine that builds the feed.
                </span>
                <button onClick={() => setRealUrl(null)}
                  className="px-3 py-1.5 text-xs rounded-lg bg-white text-gray-800 font-medium">
                  Back to editing
                </button>
              </div>
            </div>
          ) : null}

          <div ref={stageRef}
            style={{
              width: cw * scale, height: ch * scale,
              position: 'relative', overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,.45)',
            }}>
            <div style={{
              width: cw, height: ch, background: doc.canvas.bg,
              transform: `scale(${scale})`, transformOrigin: 'top left', position: 'absolute',
            }}>
              {doc.layers.map((l) => (
                <div key={l.id} style={{ position: 'absolute', inset: 0 }}>
                  <LayerView layer={l} fields={fields} productImg={productImg}
                    assets={assets} selected={l.id === selId} onMeasure={onMeasure} />
                </div>
              ))}

              {/* hizalama kılavuzları */}
              {guides?.map((g, i) => (
                <div key={i} style={{
                  position: 'absolute', background: '#f43f5e',
                  ...(g.v != null
                    ? { left: g.v, top: 0, width: 1 / scale, height: ch }
                    : { top: g.h, left: 0, height: 1 / scale, width: cw }),
                }} />
              ))}
            </div>

            {/* Tutamaklar tuvalin ÜSTÜNDE, ölçeklenmemiş katmanda: 0,3 ölçekte
                bir tutamak 3 piksele düşerse kimse yakalayamaz. */}
            {doc.layers.map((l) => {
              const b = boxOf(l);
              const isSel = l.id === selId;
              return (
                <div key={`h${l.id}`}
                  onPointerDown={(e) => onPointerDown(e, l)}
                  onDoubleClick={() => l.type === 'text' && setEditingText(true)}
                  style={{
                    position: 'absolute',
                    left: b.x * scale, top: b.y * scale,
                    width: b.w * scale, height: Math.max(b.h * scale, 8),
                    cursor: 'move',
                    border: isSel ? '1px solid #4f46e5' : '1px solid transparent',
                  }}
                  onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.border = '1px dashed #94a3b8'; }}
                  onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.border = '1px solid transparent'; }}>
                  {isSel && (l.type === 'text'
                    ? ['w', 'e'] : ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']
                  ).map((H) => (
                    <div key={H} onPointerDown={(e) => onPointerDown(e, l, H)}
                      style={{ ...handleStyle(H), cursor: `${H}-resize` }} />
                  ))}
                </div>
              );
            })}
          </div>

          {/* yazıyı yerinde düzenle */}
          {editingText && sel?.type === 'text' && (
            <InlineText layer={sel} scale={scale} stage={stageRef.current}
              onChange={(t) => patch(sel.id, { text: t }, false)}
              onDone={() => setEditingText(false)} />
          )}

          {!products.length && (
            <div className="absolute bottom-3 left-3 text-[11px] text-gray-300 bg-gray-900/70
                            rounded-lg px-2.5 py-1.5">
              Pick some products first and one of them shows up in the box while you design.
            </div>
          )}
        </div>

        {/* ------------------------------------------------------------- sağ: ayar */}
        <div className="w-72 bg-white border-l border-gray-200 overflow-y-auto shrink-0">
          {errors && (
            <div className="m-3 border border-red-200 bg-red-50 rounded-lg p-2.5">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-red-800 mb-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Not accepted
              </div>
              <ul className="text-[11px] text-red-700 list-disc list-inside space-y-0.5">
                {errors.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}

          {!sel ? (
            <div className="p-4 text-xs text-gray-400 leading-relaxed">
              Click something on the canvas to change it.
              <div className="mt-3 space-y-1 text-[11px] text-gray-400">
                <div>· drag to move · handles to resize · shift keeps the ratio</div>
                <div>· arrows nudge, shift+arrows move 10 px</div>
                <div>· double-click a text to type in place</div>
                <div>· ⌘Z undo · ⌘D duplicate · delete removes</div>
              </div>
            </div>
          ) : (
            <Inspector
              layer={sel} doc={doc} onPatch={(p) => patch(sel.id, p)}
              onRemove={() => removeLayer(sel.id)}
              onDuplicate={() => duplicateLayer(sel.id)}
              onMoveZ={(d) => moveZ(sel.id, d)}
              fields={fields}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ parçalar
const PRODUCT_FIELDS = [
  { key: 'title', label: 'Product name', icon: Type },
  { key: 'price', label: 'Current price', icon: Tag },
  { key: 'list_price', label: 'Old price', icon: Tag },
  { key: 'discount', label: 'Discount %', icon: Percent },
  { key: 'brand', label: 'Brand', icon: BadgeCheck },
];

const AddBtn = ({ icon: Icon, label, onClick, busy }) => (
  <button onClick={onClick} disabled={busy}
    className="flex flex-col items-center gap-1 py-2 rounded-lg border border-gray-200
               text-[11px] text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40">
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
    {label}
  </button>
);

const LayerIcon = ({ type }) => {
  const I = { text: Type, rect: Square, ellipse: Circle, image: ImageIcon, product: Package }[type] || Square;
  return <I className="w-3.5 h-3.5 shrink-0 opacity-60" />;
};

function handleStyle(H) {
  const s = 10;
  const pos = { position: 'absolute', width: s, height: s, background: '#fff',
    border: '1.5px solid #4f46e5', borderRadius: 2, zIndex: 2 };
  const mid = -s / 2;
  const map = {
    nw: { left: mid, top: mid }, n: { left: `calc(50% - ${s / 2}px)`, top: mid },
    ne: { right: mid, top: mid }, e: { right: mid, top: `calc(50% - ${s / 2}px)` },
    se: { right: mid, bottom: mid }, s: { left: `calc(50% - ${s / 2}px)`, bottom: mid },
    sw: { left: mid, bottom: mid }, w: { left: mid, top: `calc(50% - ${s / 2}px)` },
  };
  return { ...pos, ...map[H] };
}

/** Yazıyı tuval üstünde, aynı tipografiyle düzenletir. */
function InlineText({ layer: l, scale, stage, onChange, onDone }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  if (!stage) return null;
  const r = stage.getBoundingClientRect();
  const track = (l.tracking || 0) * l.size * scale;
  return (
    <textarea
      ref={ref} value={l.text}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onDone}
      style={{
        position: 'fixed',
        left: r.left + l.x * scale, top: r.top + l.y * scale,
        width: l.w * scale, minHeight: l.size * l.lineHeight * scale,
        fontFamily: `'${FONT_FAMILY}', system-ui, sans-serif`,
        fontWeight: l.weight, fontStyle: l.italic ? 'italic' : 'normal',
        fontSize: l.size * scale, lineHeight: `${l.size * l.lineHeight * scale}px`,
        letterSpacing: track ? `${track}px` : undefined,
        color: l.color, textAlign: l.align,
        textTransform: l.uppercase ? 'uppercase' : undefined,
        background: 'rgba(255,255,255,.92)', outline: '2px solid #4f46e5',
        border: 0, padding: 0, resize: 'none', overflow: 'hidden', zIndex: 40,
      }} />
  );
}

/** Başlangıç tasarımı seçimi — boş tuval kimseyi mutlu etmiyor. */
function StarterPicker({ onPick }) {
  const [preset, setPreset] = useState(CANVAS_PRESETS[1]);
  const starters = useMemo(() => starterDesigns(preset.w, preset.h), [preset]);
  return (
    <>
      <div className="flex flex-wrap gap-2 my-4">
        {CANVAS_PRESETS.map((p) => (
          <button key={p.key} onClick={() => setPreset(p)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition ${
              preset.key === p.key
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700 font-medium'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            {p.label} <span className="opacity-60">{p.w}×{p.h}</span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-3">
        {starters.map((s) => (
          <button key={s.key}
            onClick={() => onPick(JSON.parse(JSON.stringify(s.doc)), s.name)}
            className="text-left border border-gray-200 rounded-xl p-3 hover:border-indigo-400
                       hover:shadow-md transition group">
            <div className="rounded-lg mb-2 overflow-hidden bg-gray-100"
              style={{ aspectRatio: `${preset.w} / ${preset.h}` }}>
              <StarterThumb doc={s.doc} />
            </div>
            <div className="text-xs font-semibold text-gray-900 group-hover:text-indigo-700">{s.name}</div>
            <div className="text-[11px] text-gray-500 leading-snug mt-0.5">{s.desc}</div>
          </button>
        ))}
      </div>
    </>
  );
}

/** Başlangıç tasarımının minik önizlemesi — editörle AYNI çizim kodu, küçük
 *  ölçekte. Kart genişliği kırılıma göre değiştiği için ölçek ÖLÇÜLEREK bulunur;
 *  sabit bir sayı yazarsak dar ekranda tasarım kartından taşıyor. */
function StarterThumb({ doc }) {
  const ref = useRef(null);
  const [s, setS] = useState(0);
  const fields = useMemo(() => productFields(null, 'Your brand'), []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setS(el.clientWidth / doc.canvas.w);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [doc.canvas.w]);
  return (
    <div ref={ref} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      {s > 0 && (
        <div style={{
          width: doc.canvas.w, height: doc.canvas.h, background: doc.canvas.bg,
          position: 'absolute', transformOrigin: 'top left', transform: `scale(${s})`,
        }}>
          {doc.layers.map((l) => (
            <LayerView key={l.id} layer={l} fields={fields} productImg={null} assets={{}} />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================== ayar paneli
function Inspector({ layer: l, doc, onPatch, onRemove, onDuplicate, onMoveZ, fields }) {
  const isText = l.type === 'text';
  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-900">
          <LayerIcon type={l.type} /> {LAYER_LABEL(l)}
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={() => onMoveZ(1)} title="Bring forward"
            className="p-1.5 rounded text-gray-500 hover:bg-gray-100"><ChevronUp className="w-3.5 h-3.5" /></button>
          <button onClick={() => onMoveZ(-1)} title="Send back"
            className="p-1.5 rounded text-gray-500 hover:bg-gray-100"><ChevronDown className="w-3.5 h-3.5" /></button>
          {l.type !== 'product' && (
            <>
              <button onClick={onDuplicate} title="Duplicate (⌘D)"
                className="p-1.5 rounded text-gray-500 hover:bg-gray-100"><Copy className="w-3.5 h-3.5" /></button>
              <button onClick={onRemove} title="Delete"
                className="p-1.5 rounded text-gray-500 hover:bg-red-50 hover:text-red-600">
                <Trash2 className="w-3.5 h-3.5" /></button>
            </>
          )}
        </div>
      </div>

      {/* konum / ölçü */}
      <div className="grid grid-cols-2 gap-2 mb-2.5">
        <Row label="X"><NumBox value={l.x} onChange={(v) => onPatch({ x: v })} min={-doc.canvas.w} max={doc.canvas.w * 2} /></Row>
        <Row label="Y"><NumBox value={l.y} onChange={(v) => onPatch({ y: v })} min={-doc.canvas.h} max={doc.canvas.h * 2} /></Row>
        <Row label="Width"><NumBox value={l.w} onChange={(v) => onPatch({ w: v })} min={20} max={doc.canvas.w * 2} /></Row>
        {!isText && <Row label="Height"><NumBox value={l.h} onChange={(v) => onPatch({ h: v })} min={20} max={doc.canvas.h * 2} /></Row>}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Row label="Opacity" hint={`${Math.round((l.opacity ?? 1) * 100)}%`}>
          <input type="range" min={0} max={1} step={0.05} value={l.opacity ?? 1}
            onChange={(e) => onPatch({ opacity: parseFloat(e.target.value) })} className="w-full" />
        </Row>
        <Row label="Rotation" hint={`${l.rotation || 0}°`}>
          <input type="range" min={-180} max={180} step={1} value={l.rotation || 0}
            onChange={(e) => onPatch({ rotation: parseInt(e.target.value, 10) })} className="w-full" />
        </Row>
      </div>

      <hr className="my-3 border-gray-100" />

      {isText && (
        <>
          <Row label="Text" hint="{{price}} works">
            <textarea value={l.text} rows={3} maxLength={400}
              onChange={(e) => onPatch({ text: e.target.value })}
              className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 resize-none
                         focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </Row>
          <FieldChips fields={fields} onInsert={(k) => onPatch({ text: `${l.text}{{${k}}}` })} />

          <div className="grid grid-cols-2 gap-2 mt-2">
            <Row label="Size"><NumBox value={l.size} onChange={(v) => onPatch({ size: v })} min={6} max={400} suffix="px" /></Row>
            <Row label="Weight">
              <select value={l.weight} onChange={(e) => onPatch({ weight: parseInt(e.target.value, 10) })}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white">
                {Object.entries(WEIGHT_LABEL).map(([w, lbl]) => <option key={w} value={w}>{lbl}</option>)}
              </select>
            </Row>
          </div>

          <Row label="Colour"><ColorBox value={l.color} onChange={(v) => HEX.test(v) && onPatch({ color: v })} /></Row>
          <Row label="Align">
            <Seg value={l.align} onChange={(v) => onPatch({ align: v })}
              options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Centre' }, { value: 'right', label: 'Right' }]} />
          </Row>

          <div className="grid grid-cols-2 gap-2">
            <Row label="Line height" hint={l.lineHeight.toFixed(2)}>
              <input type="range" min={0.8} max={2} step={0.05} value={l.lineHeight}
                onChange={(e) => onPatch({ lineHeight: parseFloat(e.target.value) })} className="w-full" />
            </Row>
            <Row label="Letter space" hint={l.tracking.toFixed(2)}>
              <input type="range" min={-0.1} max={0.5} step={0.01} value={l.tracking}
                onChange={(e) => onPatch({ tracking: parseFloat(e.target.value) })} className="w-full" />
            </Row>
          </div>

          <Row label="Max lines" hint="0 = no limit">
            <NumBox value={l.maxLines} onChange={(v) => onPatch({ maxLines: v })} min={0} max={20} />
          </Row>

          <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-gray-600">
            <Check label="UPPERCASE" checked={l.uppercase} onChange={(v) => onPatch({ uppercase: v })} />
            <Check label="Italic" checked={l.italic} onChange={(v) => onPatch({ italic: v })} />
            <Check label="Crossed out" checked={l.strike} onChange={(v) => onPatch({ strike: v })} />
          </div>
        </>
      )}

      {(l.type === 'rect' || l.type === 'ellipse') && (
        <>
          <Row label="Fill"><ColorBox value={l.fill} onChange={(v) => HEX.test(v) && onPatch({ fill: v })} /></Row>
          {l.type === 'rect' && (
            <Row label="Corner radius"><NumBox value={l.radius} onChange={(v) => onPatch({ radius: v })} min={0} max={2000} suffix="px" /></Row>
          )}
          <Row label="Border"><ColorBox value={l.stroke} allowEmpty onChange={(v) => onPatch({ stroke: v })} /></Row>
          {l.stroke && (
            <Row label="Border width"><NumBox value={l.strokeWidth} onChange={(v) => onPatch({ strokeWidth: v })} min={0} max={80} suffix="px" /></Row>
          )}
        </>
      )}

      {l.type === 'image' && (
        <Row label="Fit">
          <Seg value={l.fit} onChange={(v) => onPatch({ fit: v })}
            options={[{ value: 'contain', label: 'Fit' }, { value: 'cover', label: 'Fill' }, { value: 'stretch', label: 'Stretch' }]} />
        </Row>
      )}

      {l.type === 'product' && (
        <>
          <Row label="How the product sits" hint={l.fit === 'cover' ? 'edges get cropped' : 'never cropped'}>
            <Seg value={l.fit} onChange={(v) => onPatch({ fit: v })}
              options={[{ value: 'contain', label: 'Fit' }, { value: 'cover', label: 'Fill' }, { value: 'stretch', label: 'Stretch' }]} />
          </Row>
          <Row label="Corner radius"><NumBox value={l.radius} onChange={(v) => onPatch({ radius: v })} min={0} max={2000} suffix="px" /></Row>
          <Row label="Behind the product" hint="leave empty for none">
            <ColorBox value={l.bg} allowEmpty onChange={(v) => onPatch({ bg: v })} />
          </Row>
        </>
      )}

      {/* Koşullu katman — indirimi olmayan üründe rozet BOŞ bir leke olarak
          kalmasın. Motorda da aynı kural işliyor. */}
      <hr className="my-3 border-gray-100" />
      <Row label="Only show when" hint="empty = always">
        <select value={l.showIf || ''} onChange={(e) => onPatch({ showIf: e.target.value })}
          className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white">
          <option value="">Always show</option>
          <option value="discount">The product is on sale</option>
          <option value="list_price">There is an old price</option>
          <option value="brand">The product has a brand</option>
        </select>
      </Row>
    </div>
  );
}

const Check = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-1.5 cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
      className="rounded border-gray-300" />
    {label}
  </label>
);

/** Ürün alanları — tıklayınca metnin sonuna eklenir. */
function FieldChips({ fields, onInsert }) {
  const keys = ['title', 'brand', 'price', 'list_price', 'discount', 'save'];
  return (
    <div className="flex flex-wrap gap-1 -mt-1 mb-1">
      {keys.map((k) => (
        <button key={k} onClick={() => onInsert(k)}
          title={fields[k] ? `Right now: ${fields[k]}` : 'Empty for this product'}
          className={`px-1.5 py-0.5 rounded text-[10px] font-mono border transition ${
            String(fields[k] ?? '').trim()
              ? 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              : 'border-gray-200 bg-gray-50 text-gray-400'}`}>
          {k}
        </button>
      ))}
    </div>
  );
}
